
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Star, Heart, Check, Quote,
  Briefcase, PartyPopper, Utensils, Tent, Gem
} from 'lucide-react';
import { SEO } from '../components/SEO';
import SpotlightCard from '../components/SpotlightCard.tsx';
import RevealOnScroll from '../components/RevealOnScroll.tsx';
import { SERVICES, TESTIMONIALS, SERVICE_AREAS, GOOGLE_REVIEWS_LINK } from '../constants.ts';
import { Testimonial } from '../types';

const iconMap: Record<string, React.ElementType> = {
  Heart, Briefcase, PartyPopper, Utensils, Tent, Gem, Star
};

const ReviewCard: React.FC<{ testimonial: Testimonial, index: number }> = ({ testimonial, index }) => {
  // Staggered floating animation durations for organic feel
  const animationClass = index % 2 === 0 ? 'animate-float' : 'animate-float-subtle';

  return (
    <div className={`${animationClass}`} style={{ animationDelay: `${index * 0.5}s` }}>
      <div
        className="relative p-6 md:p-8 rounded-3xl bg-white shadow-xl border border-gold-100/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-gold-500/20 hover:border-gold-300 group h-full"
      >
        <div className="absolute -top-4 -left-4 md:-top-5 md:-left-5 w-10 h-10 md:w-12 md:h-12 bg-maroon-900 rounded-2xl flex items-center justify-center shadow-lg transform -rotate-12 transition-all duration-500 group-hover:rotate-0 group-hover:scale-110 group-hover:bg-gold-500">
          <Quote className="w-5 h-5 md:w-6 md:h-6 text-gold-500 transition-colors duration-500 group-hover:text-maroon-950" />
        </div>

        <div className="flex mb-4">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-3 h-3 md:w-4 md:h-4 transition-transform duration-300 ${i < testimonial.rating ? 'text-gold-500 fill-gold-500' : 'text-gray-200'} group-hover:scale-110`}
              style={{ transitionDelay: `${i * 50}ms` }}
            />
          ))}
        </div>

        <p className="text-gray-600 italic mb-6 md:mb-8 font-light leading-relaxed text-sm md:text-base transition-colors duration-300 group-hover:text-gray-900">
          "{testimonial.text}"
        </p>

        <div className="flex items-center gap-4">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-maroon-50 flex items-center justify-center text-maroon-900 font-bold border-2 border-gold-200 text-sm md:text-base transition-all duration-500 group-hover:bg-maroon-900 group-hover:text-gold-500 group-hover:border-gold-500">
            {testimonial.name.charAt(0)}
          </div>
          <div>
            <h4 className="font-serif font-bold text-gray-900 leading-none text-sm md:text-base group-hover:text-maroon-900 transition-colors">{testimonial.name}</h4>
            <span className="text-[10px] md:text-xs text-gold-600 uppercase tracking-widest font-bold">{testimonial.location}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Home: React.FC = () => {
  const [svgPath, setSvgPath] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    const calculatePath = () => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const points = itemRefs.current
        .filter(Boolean)
        .map(item => {
          const rect = item!.getBoundingClientRect();
          return {
            x: rect.left - containerRect.left + rect.width / 2,
            y: rect.top - containerRect.top + rect.height / 2
          };
        });

      if (points.length < 2) return;

      // Create a smooth path connecting points sequentially
      let d = `M ${points[0].x} ${points[0].y}`;
      for (let i = 1; i < points.length; i++) {
        const p2 = points[i];
        d += ` L ${p2.x} ${p2.y}`;
      }
      setSvgPath(d);
    };

    calculatePath();
    window.addEventListener('resize', calculatePath);
    // Recalculate after fonts load or layout shifts
    const t = setTimeout(calculatePath, 500);
    return () => {
      window.removeEventListener('resize', calculatePath);
      clearTimeout(t);
    };
  }, []);

  return (
    <>
      <SEO
        title="Premium Event & Wedding Planner"
        description="Experience luxury event management in Midnapore & Kharagpur. From grand weddings to corporate conferences, Palmal Decorators delivers excellence."
        url="/"
      />

      {/* Hero Section */}
      <section className="relative min-h-[85vh] md:min-h-[90vh] flex items-center bg-maroon-950 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&q=80&w=2000"
            alt="Palmal Decorators Luxury Setup"
            className="w-full h-full object-cover animate-ken-burns opacity-40 blur-[1px]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-maroon-950 via-maroon-950/80 to-transparent"></div>
        </div>

        <div className="container mx-auto px-4 lg:px-12 z-10 relative py-20 md:py-0">
          <RevealOnScroll className="max-w-4xl" effect="fade-up">
            <div className="inline-flex items-center space-x-2 px-3 py-1 md:px-4 md:py-1.5 rounded-full bg-gold-500/10 border border-gold-500/20 backdrop-blur-md mb-6 md:mb-8">
              <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-gold-500 animate-pulse"></span>
              <span className="text-gold-300 text-[9px] md:text-xs font-black uppercase tracking-[0.2em]">Excellence Since 2012</span>
            </div>

            <h1 className="text-4xl xs:text-5xl md:text-7xl lg:text-8xl font-serif font-black leading-tight text-white mb-6 md:mb-8">
              We Design <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-200 via-gold-500 to-gold-200 animate-shimmer bg-[length:200%_auto]">Royal Memories</span>
            </h1>

            <p className="text-base md:text-xl lg:text-2xl text-gray-200 font-light leading-relaxed max-w-2xl mb-8 md:mb-12">
              The #1 Choice for high-profile weddings and corporate events in Paschim Medinipur. Experience the Palmal touch of perfection.
            </p>

            <div className="flex flex-col xs:flex-row gap-4 flex-wrap">
              <Link to="/contact" className="px-8 py-3 md:px-10 md:py-4 bg-gold-500 text-maroon-950 font-black rounded-lg hover:bg-gold-400 transition-all shadow-xl text-center uppercase tracking-widest text-xs md:text-sm">
                Start Planning
              </Link>
              <Link to="/gallery" className="px-8 py-3 md:px-10 md:py-4 border-2 border-white/20 text-white font-black rounded-lg hover:bg-white hover:text-maroon-950 transition-all text-center uppercase tracking-widest text-xs md:text-sm backdrop-blur-md">
                Our Portfolio
              </Link>
              <a
                href={GOOGLE_REVIEWS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3 md:px-10 md:py-4 border-2 border-gold-500/30 hover:border-gold-500 text-gold-400 font-black rounded-lg hover:bg-gold-500 hover:text-maroon-950 transition-all text-center uppercase tracking-widest text-xs md:text-sm backdrop-blur-md flex items-center justify-center gap-2 shadow-lg"
              >
                <Star className="w-4 h-4 fill-current" /> Rate Us
              </a>
            </div>
          </RevealOnScroll>
        </div>

        <div className="absolute top-1/4 -right-20 w-[200px] h-[200px] md:w-[600px] md:h-[600px] bg-gold-600/10 rounded-full blur-[80px] md:blur-[120px] animate-blob pointer-events-none"></div>
        <div className="absolute -bottom-20 -left-20 w-[200px] h-[200px] md:w-[600px] md:h-[600px] bg-maroon-600/10 rounded-full blur-[80px] md:blur-[120px] animate-morph pointer-events-none"></div>
      </section>

      {/* Reviews Section - Added above Services */}
      <section className="py-16 md:py-24 bg-cream-50 overflow-hidden relative">
        <div className="container mx-auto px-4 lg:px-12 max-w-[1800px] relative z-10">
          <RevealOnScroll effect="fade-up" className="text-center mb-12 md:mb-20">
            <h3 className="text-gold-600 font-black uppercase tracking-[0.3em] text-[10px] md:text-sm mb-3 md:mb-4">Client Feedback</h3>
            <h2 className="text-3xl md:text-5xl font-serif font-black text-maroon-900">Voices of Satisfaction</h2>
          </RevealOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 lg:gap-16 pt-4 md:pt-8">
            {TESTIMONIALS.map((testimonial, idx) => (
              <ReviewCard key={testimonial.id} testimonial={testimonial} index={idx} />
            ))}
          </div>

          <div className="text-center mt-12 md:mt-20">
            <a
              href={GOOGLE_REVIEWS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 text-maroon-900 font-black uppercase tracking-widest text-xs md:text-sm hover:text-gold-600 transition-colors group"
            >
              Read All Reviews on Google <ArrowRight className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-2 transition-transform" />
            </a>
          </div>
        </div>

        {/* Background decorative elements */}
        <div className="absolute top-1/2 left-0 -translate-x-1/2 w-64 h-64 md:w-96 md:h-96 bg-gold-200/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 translate-x-1/4 w-80 h-80 md:w-[500px] md:h-[500px] bg-maroon-100/30 rounded-full blur-3xl pointer-events-none"></div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 lg:py-40 bg-white relative">
        <div className="container mx-auto px-4 lg:px-12 max-w-[1800px]">
          <RevealOnScroll className="text-center mb-16 md:mb-24 max-w-3xl mx-auto" effect="fade-up">
            <h2 className="text-3xl md:text-6xl font-serif font-black text-maroon-900 mb-6 md:mb-8">Premium Services</h2>
            <p className="text-gray-500 text-base md:text-xl font-light">From intimate bridal shows to grand corporate summits, we bring a decade of expertise to every detail.</p>
          </RevealOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-12">
            {SERVICES.map((service, idx) => {
              const Icon = iconMap[service.iconName] || Star;
              return (
                <RevealOnScroll key={service.id} delay={idx * 100} effect="fade-up" className="h-full">
                  <SpotlightCard className="h-full group hover:-translate-y-2 transition-all duration-500 border border-gray-100 shadow-xl hover:shadow-gold-500/10 rounded-2xl overflow-hidden">
                    <div className="p-8 md:p-10 flex flex-col h-full">
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-maroon-50 flex items-center justify-center mb-8 md:mb-10 group-hover:bg-maroon-900 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3">
                        <Icon className="w-6 h-6 md:w-8 md:h-8 text-maroon-900 group-hover:text-gold-500 transition-all duration-500 group-hover:-rotate-3 group-hover:scale-110" />
                      </div>
                      <h3 className="text-xl md:text-3xl font-serif font-black text-gray-800 mb-4 md:mb-6 group-hover:text-maroon-900 transition-colors">{service.title}</h3>
                      <p className="text-gray-500 text-base md:text-lg leading-relaxed mb-8 md:mb-10 font-light flex-grow">{service.shortDescription}</p>
                      <Link to={`/services/${service.id}`} className="inline-flex items-center text-gold-600 font-black text-xs md:text-sm uppercase tracking-[0.2em] group-hover:text-gold-500 transition-all gap-2">
                        Explore Service <ArrowRight className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-2 transition-transform" />
                      </Link>
                    </div>
                  </SpotlightCard>
                </RevealOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      {/* Interactive Network Map Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-maroon-950 text-white relative overflow-hidden">
        {/* Styles for Wave Animation */}
        <style>{`
          @keyframes wave {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .water-wave {
            animation: wave 25s linear infinite;
          }
          .water-wave-fast {
            animation: wave 15s linear infinite reverse;
          }
        `}</style>

        {/* Dynamic Water Background */}
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-maroon-950 via-maroon-900 to-maroon-950 opacity-90"></div>

          {/* Wave 1 */}
          <div className="absolute bottom-0 left-0 w-[200%] h-64 flex water-wave opacity-20">
            <svg className="w-1/2 h-full" viewBox="0 0 1440 320" preserveAspectRatio="none">
              <path fill="#c5a059" d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
            <svg className="w-1/2 h-full" viewBox="0 0 1440 320" preserveAspectRatio="none">
              <path fill="#c5a059" d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
          </div>

          {/* Wave 2 */}
          <div className="absolute bottom-[-20px] left-0 w-[200%] h-64 flex water-wave-fast opacity-10">
            <svg className="w-1/2 h-full" viewBox="0 0 1440 320" preserveAspectRatio="none">
              <path fill="#ffffff" d="M0,128L48,144C96,160,192,192,288,181.3C384,171,480,117,576,106.7C672,96,768,128,864,144C960,160,1056,160,1152,144C1248,128,1344,96,1392,80L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
            <svg className="w-1/2 h-full" viewBox="0 0 1440 320" preserveAspectRatio="none">
              <path fill="#ffffff" d="M0,128L48,144C96,160,192,192,288,181.3C384,171,480,117,576,106.7C672,96,768,128,864,144C960,160,1056,160,1152,144C1248,128,1344,96,1392,80L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
          </div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <h3 className="text-gold-500 text-xs md:text-sm font-black uppercase tracking-[0.4em] mb-4">Coverage Areas</h3>
          <h2 className="text-3xl md:text-5xl font-serif font-bold mb-10 md:mb-16 bg-gradient-to-r from-gold-300 via-white to-gold-300 bg-[length:200%_auto] bg-clip-text text-transparent animate-gradient-move">Bringing Joy to Your Neighborhood</h2>

          <div className="relative py-8 md:py-12" ref={containerRef}>
            {/* SVG Background Layer for Connections */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-0 overflow-visible">
              <defs>
                <linearGradient id="pathGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#c5a059" stopOpacity="0.2" />
                  <stop offset="50%" stopColor="#c5a059" stopOpacity="1" />
                  <stop offset="100%" stopColor="#c5a059" stopOpacity="0.2" />
                </linearGradient>
              </defs>

              {/* Static background path for structure */}
              {svgPath && (
                <path
                  d={svgPath}
                  stroke="#ffffff"
                  strokeOpacity="0.1"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {/* Animated Flowing Line */}
              {svgPath && (
                <path
                  d={svgPath}
                  stroke="url(#pathGradient)"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="animate-flow-dash"
                  style={{
                    strokeDasharray: '20 10',
                  }}
                />
              )}
            </svg>

            <div className="flex flex-wrap justify-center gap-4 md:gap-8 relative z-10">
              {SERVICE_AREAS.map((area, index) => (
                <span
                  key={area}
                  ref={el => { itemRefs.current[index] = el; }}
                  className="px-6 py-3 md:px-8 md:py-4 rounded-2xl text-xs md:text-base font-bold transition-all duration-300 bg-maroon-900/60 backdrop-blur-sm border border-gold-500/30 text-gold-100 shadow-lg hover:scale-105 hover:bg-maroon-800 hover:border-gold-500 hover:text-white cursor-default"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 md:py-24 lg:py-40 bg-cream-100">
        <div className="container mx-auto px-4 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h3 className="text-gold-600 font-black uppercase tracking-widest text-xs md:text-sm mb-4">Why Choose Us</h3>
              <h2 className="text-3xl md:text-5xl font-serif font-black text-maroon-900 mb-8">Crafting Grandeur Since a Decade</h2>
              <div className="space-y-6">
                {[
                  "Award-winning floral and stage designers",
                  "Professional catering with authentic cuisines",
                  "On-time execution guaranteed for all sizes",
                  "Transparent pricing with no hidden costs"
                ].map((item, i) => (
                  <div key={i} className="flex items-start">
                    <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-gold-500 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <Check className="w-3 h-3 md:w-4 md:h-4 text-maroon-950" />
                    </div>
                    <p className="text-gray-700 text-base md:text-xl font-light">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gold-500/20 rounded-full blur-[100px] -z-10 animate-pulse-slow"></div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                <img
                  src="https://lh3.googleusercontent.com/d/11O3bVng4N817HAsKoaIRbrcGxw1D1_Vm"
                  className="w-full h-auto object-cover"
                  alt="Palmal Decorators Premium Event Setup"
                  onError={(e) => {
                    e.currentTarget.src = "https://images.unsplash.com/photo-1519225421980-715cb0202128?auto=format&fit=crop&q=80&w=1200";
                    e.currentTarget.alt = "Event Setup (Fallback)";
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
